/* eslint-disable no-nested-ternary */
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LazyLoad from 'react-lazyload';
import { GET_POST_BODY } from '../../helpers/graphql/singlePost';
import graphQLClient from '../../helpers/graphQLClient';
import parseHtmlToReact from '../../helpers/parseHtmlToReact';
import { getUser } from '../../helpers/token';
import { getLocationDetails } from 'helpers/getLocationDetails';
import { fraunces } from '../Landing/fonts';
import SimpleHeader from 'components/Blog/SimpleHeader';
import ArticleBody from './Article/ArticleBody';
import ArticleHeader from './Article/ArticleHeader';
import ArticleHero from './Article/ArticleHero';
import ArticleMasthead from './Article/ArticleMasthead';
import AuthorCard from './Article/AuthorCard';
import ConversionRail from './Article/ConversionRail';
import DestinationCard from './Article/DestinationCard';
import RelatedPosts from './Article/RelatedPosts';
import StartBlogCta from './Article/StartBlogCta';
import CommentItem from './Comments/CommentItem';
import PostComments from './Comments/PostComments';
import ExternalLinkModal from './ExternalLinkModal';
import LargeSocialShareDialog from './LargeSocialShareDialog';
import MembersOnly from './MembersOnly';
import PostSocialShares from './PostSocialShares';
import StickyVoteSlider from './StickyVoteSlider';
import VoteDetailsBtn from './VoteDetailsBtn';

const VideoHeader = dynamic(() => import('./VideoHeader'), {
  ssr: false,
  loading: () => <Skeleton variant="rectangular" width="100%" height={400} />,
});
const PostLightbox = dynamic(() => import('./PostLightbox'), {
  ssr: false,
  loading: () => <></>,
});

const SinglePost = props => {
  const { hostingUrl, isSimpleCover } = props;
  const {
    location,
    title,
    excerpt,
    createdAt,
    tags,
    author,
    isTf,
    permlink,
    _id,
    comments,
    totalSmiles,
    smiles,
    hasData,
    readTime,
    community,
    youtubeId,
    blacklist,
    allImgDetails,
    cover,
    thumb,
    isUgcLinks,
    heroMode,
  } = props.post;

  const theme = useTheme();

  // Paying members' posts carry no promos at all — not in the article, not
  // below it, not on the rail. Their readers are their audience, not ours.
  const authorIsPaid = Boolean(
    author && author.roles && author.roles.indexOf('paid') !== -1,
  );

  const [body, setMemberBody] = useState(props.post.body);
  const [editLink, setEditLink] = useState(undefined);
  const [state, setState] = useState({
    userComment: undefined,
    lightboxIsOpen: false,
    lightboxIndex: 0,
  });
  const [externalLinkModal, setExternalLinkModal] = useState(undefined);

  const getMembersOnlyBody = () => {
    graphQLClient(GET_POST_BODY, {
      author: author.username,
      permlink,
    })
      .then(res => {
        if (res && res.post && res.post.body) setMemberBody(res.post.body);
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err));
  };

  useEffect(() => {
    if (!body && blacklist && blacklist.isMembersOnly && getUser())
      getMembersOnlyBody();
    if (getUser() === author.username)
      setEditLink(
        `/dashboard/publish?permlink=${encodeURIComponent(permlink)}`,
      );
  }, []);

  // Warm paper page background (rule in styles/style.css), matching the
  // landing page aesthetic.
  useEffect(() => {
    document.body.classList.add('tf-paper');
    return () => document.body.classList.remove('tf-paper');
  }, []);

  useEffect(() => {
    if (props.post.body) setMemberBody(props.post.body);
  }, [props.post.body]);

  const toggleModal = () => {
    setState(prev => ({ ...prev, lightboxIsOpen: !prev.lightboxIsOpen }));
  };

  const toggleLightbox = useCallback(lightboxIndex => {
    setState(prev => ({
      ...prev,
      lightboxIsOpen: !prev.lightboxIsOpen,
      lightboxIndex,
    }));
  }, []);

  const onCommentAdd = userComment => {
    // eslint-disable-next-line no-param-reassign
    userComment.depth = 1;
    setState(prev => ({ ...prev, userComment }));
  };

  // Parse the post body exactly once per body — webp support, card width
  // and window height no longer trigger full re-parses.
  const reactParsed = useMemo(
    () =>
      parseHtmlToReact(body || '', {
        hideimgcaptions: !isTf,
        toggleLightbox,
        webpSupport: true,
        allImgDetails,
        showAffiliateBox: false,
        // In-article conversion modules (start-a-blog postcard, Menu
        // Translator) — inserted between top-level paragraphs of longer
        // posts; both hide themselves for logged-in members. Posts written
        // by paying (verified) members never carry in-article promos, and
        // neither do internal /blog posts (isSimpleCover).
        inlinePromos: !isSimpleCover && !authorIsPaid,
        thumbUrl: thumb && thumb.url ? thumb.url : undefined,
        countryCode: location ? location.countryCode : undefined,
        setExternalLinkModal,
        isUgcLinks,
        // The cover is the priority/LCP image; body images load lazily.
        hasHero: Boolean(
          !youtubeId && ((cover && cover.url) || (thumb && thumb.url)),
        ),
        // Don't repeat the hero image at the top of the body.
        skipImageUrl:
          !youtubeId && !isSimpleCover
            ? (cover && cover.url) || (thumb && thumb.url)
            : undefined,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [body],
  );
  const { bodyText } = reactParsed;

  const { locationTitle, country } = getLocationDetails(location);

  const isMembersTeaser = !body && blacklist && blacklist.isMembersOnly;

  if (!body && !isMembersTeaser && !youtubeId)
    return <div style={{ height: '100vh' }} />;

  let coverImage;
  if (cover && cover.url) coverImage = cover;
  if (!coverImage && thumb && thumb.url) coverImage = thumb;

  const hasComments = Boolean(state.userComment || (comments && comments > 0));

  const commentsBlock = (
    <section className="tf-comments tw-mx-auto tw-max-w-[46rem] tw-px-4 tw-pb-12">
      <div className="anchor" id="comments" />
      {hasComments && (
        <h2
          className={`${fraunces.className} tw-mb-6 tw-mt-0 tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] sm:tw-text-3xl`}
        >
          Comments
        </h2>
      )}
      {state.userComment && (
        <div className="tw-pb-2">
          <CommentItem comment={state.userComment} hideReplyBtn />
        </div>
      )}
      {comments && comments > 0 ? (
        <LazyLoad once offset={800} height={200}>
          <PostComments rootId={_id} />
        </LazyLoad>
      ) : null}
      <style jsx global>{`
        /* Comments inherit the article's paper-card language instead of
           heavy MUI shadows. */
        .tf-comments .MuiPaper-root {
          border-radius: 14px;
          box-shadow: none;
          border: 1px solid #e7e0cf;
          background-color: #fffdf6;
        }
        .tw-dark .tf-comments .MuiPaper-root {
          border-color: #26382f;
          background-color: #15231e;
        }
        .tf-comments .MuiPaper-root .MuiPaper-root {
          border: 0;
        }
        /* Comment bodies (parsed user HTML) get the article's voice. */
        .tf-comments img {
          border-radius: 10px;
        }
        .tf-comments .postcontent blockquote,
        .tf-comments blockquote {
          border: 0;
          border-left: 3px solid #00897b;
          margin: 1.2em 0;
          padding: 0.2em 0 0.2em 1.1em;
          font-style: italic;
          color: rgba(29, 45, 40, 0.75);
        }
        .tw-dark .tf-comments blockquote {
          color: rgba(235, 230, 217, 0.8);
        }
        .tf-comments .fullwidth {
          width: 100% !important;
          max-width: 100% !important;
          margin: 1em 0 !important;
          padding: 0;
        }
        /* Material buttons inside comments (Reply, load more) become quiet
           editorial actions. */
        .tf-comments .MuiButton-root {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.12em;
          color: #00796b;
          border-radius: 9999px;
        }
        .tf-comments .MuiButton-root:hover {
          background-color: rgba(0, 150, 136, 0.08);
        }
        .tw-dark .tf-comments .MuiButton-root {
          color: #80cbc4;
        }
      `}</style>
    </section>
  );

  return (
    <>
      {isSimpleCover && (
        <SimpleHeader
          tags={tags}
          author={author}
          title={title}
          coverImage={coverImage}
          createdAt={createdAt}
          youtubeId={youtubeId}
        />
      )}

      <article>
        <div id="post">
          {/* Wide, high-res covers earn the cinematic masthead (photo IS
              the header); everything else keeps the paper header, followed
              by the video frame or the full-bleed journal-window cover.
              The mode is decided server-side in helpers/articleHeroMode.js. */}
          {!isSimpleCover && heroMode === 'cinematic' && coverImage ? (
            <ArticleMasthead
              coverImage={coverImage}
              title={title}
              location={location}
              author={author}
              createdAt={createdAt}
              readTime={readTime}
              permlink={permlink}
              thumbnail={thumb ? thumb.url : undefined}
              tags={tags}
              editLink={editLink}
            />
          ) : (
            <>
              {!isSimpleCover && (
                <ArticleHeader
                  title={title}
                  author={author}
                  createdAt={createdAt}
                  readTime={readTime}
                  location={location}
                  community={community}
                  permlink={permlink}
                  thumbnail={thumb ? thumb.url : undefined}
                  tags={tags}
                  editLink={editLink}
                />
              )}

              {!isSimpleCover && youtubeId && (
                <div className="tw-mx-auto tw-mb-8 tw-max-w-5xl sm:tw-px-4">
                  <div className="tw-overflow-hidden tw-shadow-[0_30px_60px_-25px_rgba(29,45,40,0.4)] sm:tw-rounded-[20px]">
                    <VideoHeader
                      windowHeight={760}
                      youtubeId={youtubeId}
                      isSound
                    />
                  </div>
                </div>
              )}

              {!isSimpleCover && !youtubeId && coverImage && (
                <ArticleHero
                  coverImage={coverImage}
                  title={title}
                  location={location}
                />
              )}
            </>
          )}

          <ArticleBody>
            {isSimpleCover && <div className="tw-pt-6" />}
            {isMembersTeaser ? (
              <>
                <div className="fadeOutBody">{excerpt}</div>
                <div className="fadeOut" />
                <div className="fadeOutOver">
                  <MembersOnly hostingUrl={hostingUrl} author={author} />
                </div>
              </>
            ) : body && bodyText ? (
              bodyText
            ) : youtubeId ? (
              <></>
            ) : (
              Array.from({ length: 20 }, (_, key) => (
                <Skeleton key={key} variant="text" width="100%" height={25} />
              ))
            )}
          </ArticleBody>

          {/* Tags, smiles and shares */}
          {hasData && (
            <div className="tw-mx-auto tw-max-w-[46rem] tw-px-4 tw-pt-2">
              {tags && tags.length > 0 && (
                <div className="tw-flex tw-flex-wrap tw-gap-2 tw-pb-4">
                  {tags.slice(0, 8).map(tag => (
                    <Link
                      key={tag}
                      href={`/topics/${tag}`}
                      prefetch={false}
                      className="tw-rounded-full tw-border tw-border-solid tw-border-[#D8D0BC] dark:tw-border-[#3A4D44] tw-px-3 tw-py-1 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.15em] tw-text-[#5F6F69] dark:tw-text-[#93A69E] tw-no-underline tw-transition-colors hover:tw-border-primary-600 hover:tw-text-primary-700 dark:hover:tw-text-primary-300"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              )}
              <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-black/10 dark:tw-border-white/10 tw-pt-4">
                <VoteDetailsBtn
                  numberreplies={comments}
                  votes={smiles}
                  totalSmiles={totalSmiles}
                />
                <PostSocialShares
                  author={author.username}
                  permlink={permlink}
                  tags={tags}
                  title={title}
                  thumbnail={thumb ? thumb.url : undefined}
                />
              </div>
            </div>
          )}
        </div>

        {(isSimpleCover || youtubeId) && smiles !== undefined && (
          <div className="tw-mx-auto tw-max-w-[46rem] tw-px-4">
            <StickyVoteSlider
              isRelative
              author={author.username}
              permlink={permlink}
              id={_id}
              title={title}
              smiles={smiles}
              totalSmiles={totalSmiles}
              noOfComments={comments}
              onCommentAdd={onCommentAdd}
              locationTitle={locationTitle}
            />
          </div>
        )}

        {!isSimpleCover && (
          <AuthorCard author={author} currentPermlink={permlink} hideOnRail />
        )}

        {/* Start-a-blog card — never on a paying member's post (their own
            readers are not our funnel), like every other promo here. */}
        {!isSimpleCover && !authorIsPaid && (
          <StartBlogCta
            country={country}
            thumbUrl={thumb && thumb.url ? thumb.url : undefined}
          />
        )}

        {!isSimpleCover && (
          <ConversionRail
            country={country}
            thumbUrl={thumb && thumb.url ? thumb.url : undefined}
            location={location}
            author={author}
          />
        )}

        {!isSimpleCover && (
          <LazyLoad once offset={700} height={420}>
            {/* On very wide screens the rail shows the map instead. */}
            <DestinationCard location={location} hideOnRail />
          </LazyLoad>
        )}

        {commentsBlock}

        {!isSimpleCover && (
          <LazyLoad once offset={900} height={420}>
            <RelatedPosts
              community={community}
              countryCode={location ? location.countryCode : undefined}
              coordinates={
                location && location.coordinates
                  ? location.coordinates.coordinates
                  : undefined
              }
              currentPermlink={permlink}
            />
          </LazyLoad>
        )}
      </article>

      {!youtubeId && !isSimpleCover && smiles !== undefined && (
        <StickyVoteSlider
          author={author.username}
          permlink={permlink}
          id={_id}
          title={title}
          smiles={smiles}
          totalSmiles={totalSmiles}
          noOfComments={comments}
          onCommentAdd={onCommentAdd}
          locationTitle={locationTitle}
        />
      )}

      {allImgDetails && allImgDetails.length > 0 && (
        <PostLightbox
          lightboxIsOpen={state.lightboxIsOpen}
          toggleModal={toggleModal}
          images={allImgDetails}
          lightboxIndex={state.lightboxIndex}
          isWebp
        />
      )}
      <LargeSocialShareDialog
        author={author.username}
        permlink={permlink}
        tags={tags}
        title={title}
        thumbnail={thumb ? thumb.url : undefined}
        createdAt={createdAt}
      />
      <ExternalLinkModal
        externalLinkModal={externalLinkModal}
        setExternalLinkModal={setExternalLinkModal}
      />
      <style jsx>{`
        .fadeOutBody {
          height: 180px;
          position: relative;
          overflow: hidden;
        }
        .fadeOut {
          top: -180px;
          position: relative;
          height: 180px;
          width: 100%;
          background-image: linear-gradient(
            to bottom,
            transparent,
            ${theme.palette.mode === 'dark' ? '#0E1A16' : '#FAF8F1'}
          );
        }
        .fadeOutOver {
          width: 100%;
          position: relative;
          margin-top: -140px;
          margin-bottom: 60px;
        }
      `}</style>
    </>
  );
};

export default SinglePost;
